/**
 *  @swagger
 *  definitions:
 *    RegattaScheduleParams:
 *      type: object
 *      properties:
 *        RegattaId:
 *          description: ID of the associated Regatta
 *          type: string
 *        Name:
 *          description: Name of the RegattaSchedule
 *          type: string
 *        Content:
 *          description: Content of the RegattaSchedule
 *          type: string
 *        StartDate:
 *          description: Start Date of the RegattaSchedule
 *          type: string
 *          format: date-time
 *        EndDate:
 *          description: End Date of the RegattaSchedule
 *          type: string
 *          format: date-time
 *
 *  @swagger
 *  definitions:
 *    RegattaScheduleCreateParams:
 *      allOf:
 *        - $ref: '#/definitions/RegattaScheduleParams'
 *        - type: object
 *          required:
 *            - RegattaId
 *            - Name
 *            - StartDate
 *
 *  @swagger
 *  definitions:
 *    RegattaScheduleQueryParams:
 *      allOf:
 *        - type: object
 *          properties:
 *            id:
 *              description: ID of the RegattaSchedule
 *              type: string
 *        - $ref: '#/definitions/RegattaScheduleParams'
 *
 *  @swagger
 *  definitions:
 *    RegattaScheduleUpdateParams:
 *      allOf:
 *        - $ref: '#/definitions/RegattaScheduleQueryParams'
 *        - type: object
 *          required:
 *            - id
 *
 *  @swagger
 *  definitions:
 *    RegattaSchedule:
 *      allOf:
 *        - $ref: '#/definitions/RegattaScheduleCreateParams'
 *        - $ref: '#/definitions/RegattaScheduleUpdateParams'
 *        - $ref: '#/definitions/Timestamps'
 *
 *  @swagger
 *  components:
 *    schemas:
 *      RegattaSchedule:
 *        allOf:
 *          - $ref: '#/definitions/RegattaSchedule'
 *          - type: object
 *            properties:
 *              Regatta:
 *                description: Regatta of the RegattaSchedule
 *                $ref: '#/components/schemas/Regatta'
 */

// Types
import type {
  $Model,
  $ModelClass,
  $ModelClasses,
} from '.';
import type {
  $Field,
} from '../types';

export type $RelationIds = {
  RegattaId: string;
};

export type $Params = {
  Content: null | string;
  EndDate: Date | null;
  Name: string;
  StartDate: Date;
};

export type $CreateParams = $Params & $RelationIds;

export type $UpdateParams = {
  id: string;
} & Partial<$CreateParams>;

export type $UpsertParams = $CreateParams & {
  id?: string;
};

export type $QueryParams = {
  id?: null | string;
} & Partial<$CreateParams>;

export type $Associations = {
  Regatta?: $Model<'Regatta'>;
};

export type $ModelParams = $Associations & $CreateParams & {
  id: string;
};

type $Fields = $Params & {
  id: string;
};

const fields = (
  Sequelize,
): Record<keyof $Fields, $Field> => ({
  Content: {
    allowNull: true,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  EndDate: {
    allowNull: true,
    type: Sequelize.DATE,
    validate: {
      notEmpty: true,
    },
  },
  id: {
    allowNull: false,
    defaultValue: Sequelize.UUIDV4,
    primaryKey: true,
    type: Sequelize.UUID,
    unique: true,
  },
  Name: {
    allowNull: false,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  StartDate: {
    allowNull: false,
    type: Sequelize.DATE,
    validate: {
      notEmpty: true,
    },
  },
});

export default ((
  Sequelize,
  stack,
): $ModelClass<'RegattaSchedule'> => {
  const RegattaSchedule = stack.define(
    'RegattaSchedule',
    fields(Sequelize),
    {
      tableName: 'RegattaSchedules',
    },
  );

  RegattaSchedule.associate = (models: $ModelClasses) => {
    models.RegattaSchedule.belongsTo(
      models.Regatta,
      {
        as: 'Regatta',
        foreignKey: 'RegattaId',
      },
    );
  };

  RegattaSchedule.addScope(
    'defaultScope',
    {
      order: [['StartDate', 'ASC']],
    },
  );

  return RegattaSchedule;
});
